"use client";

import type { Session } from "@onsand/api";
import {
	adminApi,
	clearClientToken,
	setClientToken,
	setTokenRefresher,
} from "@onsand/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { onIdTokenChanged } from "firebase/auth";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { auth } from "./firebase";

const SessionContext = createContext<{
	session: Session | null;
	isLoading: boolean;
	refreshSession: () => Promise<void>;
} | null>(null);

export function SessionProvider({
	children,
	queryFn,
}: {
	children: React.ReactNode;
	queryFn?: () => Promise<Session>;
}) {
	const queryClient = useQueryClient();
	const [ready, setReady] = useState(false);

	useEffect(() => {
		setTokenRefresher(async () => {
			// Until Firebase has restored the stored session, currentUser is null for a signed-in
			// visitor too; a request that 401s on page load must not read that as "signed out".
			await auth.authStateReady();
			const user = auth.currentUser;
			if (!user) return null;
			return user.getIdToken(true);
		});

		const unsubscribe = onIdTokenChanged(auth, async (user) => {
			if (user) {
				const token = await user.getIdToken();
				setClientToken(token);
			} else {
				clearClientToken();
			}
			setReady(true);
			queryClient.invalidateQueries({ queryKey: ["session"] });
		});

		return unsubscribe;
	}, [queryClient]);

	const { data: session, isLoading } = useQuery({
		queryKey: ["session"],
		queryFn: queryFn ?? (() => adminApi.session.me()),
		enabled: ready,
		retry: 1,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	const refreshSession = useCallback(async () => {
		const user = auth.currentUser;
		if (user) {
			await user.getIdToken(true);
		}
	}, []);

	return (
		<SessionContext
			value={{ session: session ?? null, isLoading: isLoading || !ready, refreshSession }}
		>
			{children}
		</SessionContext>
	);
}

export function useSession() {
	const ctx = useContext(SessionContext);
	if (!ctx) throw new Error("useSession must be used within SessionProvider");
	return ctx;
}
